import React, { useState, useEffect } from 'react';
import { X, Sparkles, Trophy, Gift, Star } from 'lucide-react';
import { userAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const CelebrationNotification = ({ user, onClose }) => {
  const { refreshUserData } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (user?.email) {
      loadNotifications();
    }
  }, [user]);

  const loadNotifications = async () => {
    try {
      const notifs = await userAPI.getNotifications(user.email);
      if (notifs.length > 0) {
        setNotifications(notifs);
        setCurrentNotification(notifs[0]); // Show the first/latest notification
        setIsVisible(true);
        
        // When showing celebration notification, also refresh user data
        // This ensures expiration warnings are updated after key renewal
        try {
          await refreshUserData();
        } catch (error) {
          console.log('Note: Could not refresh user data during notification display');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    }
  };

  const handleClose = async () => {
    if (currentNotification) {
      try {
        await userAPI.markNotificationAsRead(currentNotification.id);
        setIsVisible(false);
        
        // Show next notification after 500ms if available
        setTimeout(() => {
          const remainingNotifications = notifications.filter(n => n.id !== currentNotification.id);
          if (remainingNotifications.length > 0) {
            setCurrentNotification(remainingNotifications[0]);
            setIsVisible(true);
          } else {
            onClose && onClose();
          }
        }, 500);
      } catch (error) {
        console.error('Erro ao marcar notificação como lida:', error);
      }
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'celebration':
        return <Trophy className="w-8 h-8 text-yellow-400" />;
      case 'success':
        return <Star className="w-8 h-8 text-green-400" />;
      case 'gift':
        return <Gift className="w-8 h-8 text-purple-400" />;
      default:
        return <Sparkles className="w-8 h-8 text-blue-400" />;
    }
  };

  if (!isVisible || !currentNotification) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="relative max-w-lg w-full">
        {/* Background with celebration effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-2xl blur-sm opacity-75 animate-pulse"></div>
        
        {/* Main notification card */}
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl border-4 border-gradient-to-r from-yellow-400 to-pink-400">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Celebration header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-full mb-4 animate-bounce">
              {getIcon(currentNotification.type)}
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {currentNotification.title}
            </h2>
          </div>

          {/* Message content */}
          <div className="text-center mb-8">
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
              {currentNotification.message}
            </p>
          </div>

          {/* Decorative elements */}
          <div className="flex justify-center items-center space-x-4 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-full"></div>
            <Sparkles className="w-6 h-6 text-yellow-500 animate-spin" />
            <div className="w-12 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"></div>
          </div>

          {/* Action button */}
          <div className="text-center">
            <button
              onClick={handleClose}
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-semibold rounded-full hover:from-green-600 hover:to-teal-600 transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              <Trophy className="w-5 h-5 mr-2" />
              Continuar Lucrando!
            </button>
          </div>

          {/* Celebration confetti effect */}
          <div className="absolute -top-4 -left-4">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
          </div>
          <div className="absolute -top-2 -right-6">
            <div className="w-2 h-2 bg-pink-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
          </div>
          <div className="absolute -bottom-4 -left-6">
            <div className="w-4 h-4 bg-purple-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
          </div>
          <div className="absolute -bottom-2 -right-4">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-ping" style={{ animationDelay: '1.5s' }}></div>
          </div>
        </div>

        {/* Additional celebration text */}
        {notifications.length > 1 && (
          <div className="text-center mt-4">
            <p className="text-white text-sm opacity-75">
              {notifications.length - 1} notificação(ões) adicional(is)
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CelebrationNotification;